import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';
import isEqual from 'react-fast-compare';

import {ARCHIVED_ANALYSIS_TOOLTIP_TEXT} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import isAnalysisRunning from 'analysis-tool/isAnalysisRunning';
import {UseSelectedAnalysisItemOutput} from 'analysis-tool/ui/hooks/useSelectedAnalysisItem';
import {StepInitializationAnalysisMainViewItem} from 'analysis-tool/ui/main-view/MainViewTypes';
import {
  isStepAwareAnalysisItem,
  isViewStepConfigAnalysisItem,
} from 'analysis-tool/ui/main-view/MainViewTypes';
import {trackEvent} from 'analytics/Analytics';
import isArchived from 'archive/isArchived';
import {ANALYSIS_TOOL_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARKER} from 'ui-library/Colors';

import AddChartViewPopover from '../charting/AddChartViewPopover';
import AddViewButton from './AddViewButton';
import SidebarItem from './SidebarItem';
import {SidebarItemData} from './SidebarItemDatas';

/** @package */
export interface SidebarSectionItemsProps {
  analysis: AnalysisGraphQL;
  sidebarItemDatas: Array<SidebarItemData>;
  selectedItemState: UseSelectedAnalysisItemOutput;
  onOpenStepInitializationView: (newSelectedItem: StepInitializationAnalysisMainViewItem) => void;
  tier?: number;
}

/**
 * Render child `SidebarItem`s based on `sidebarItemDatas` and `selectedItemState`.
 *
 * If a `sidebarItemData` has `childSidebarItems`, recursively render `SidebarSectionItems` with
 * 20px of margin to render the next tier.
 *
 * ```
 * sidebarItemData
 *   childSidebarItem 1
 *   childSidebarItem 2
 *     child item of childSidebarItem 2
 * sidebarItemData
 * ```
 *
 * @param tier Internal-only prop for tracking the current tier when recursing over `childSidebarItems` for use
 * with the `key`.
 * @package
 */
export function SidebarSectionItems({
  analysis,
  sidebarItemDatas,
  selectedItemState,
  tier = 0,
  onOpenStepInitializationView,
}: SidebarSectionItemsProps): JSX.Element {
  return (
    <>
      {sidebarItemDatas.map(
        (
          {
            icon,
            title,
            mainViewItem,
            childSidebarItems = [],
            actionButtonOptions,
            disabled,
            tooltipText,
            subtitleText,
            isFlaggingRows = false,
          },
          index
        ) => (
          // Using the index for the key isn't ideal, but should be sufficient for now in lieu of writing a
          // mapping from mainViewItem -> key.
          // If SidebarItem starts using hooks, we should revisit this since the "0th" item in this
          // array may switch from referring to a status page to referring to, say, a dataset once the step
          // completes and so "0" is not a good unique key.
          <div key={`${tier}-${index}`}>
            <SidebarItem
              icon={icon}
              isSelected={
                isEqual(selectedItemState.selectedItem, mainViewItem) ||
                (isViewStepConfigAnalysisItem(selectedItemState.selectedItem) &&
                  isStepAwareAnalysisItem(mainViewItem) &&
                  selectedItemState.selectedItem.stepId === mainViewItem.stepId)
              }
              onSelect={() => {
                trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.SELECTED_SIDEBAR_ITEM, {
                  previousItem: selectedItemState.selectedItem,
                  newItem: mainViewItem,
                });
                selectedItemState.onChangeSelectedItem(mainViewItem);
              }}
              title={title}
              disabled={disabled}
              tooltipText={tooltipText}
              subtitleText={subtitleText}
              isFlaggingRows={isFlaggingRows}
              dataTestAttrsKey={`${tier}-${index}`}
            />
            {childSidebarItems.length > 0 && (
              <div className={css(styles.childSidebarItemsWrapper)}>
                <SidebarSectionItems
                  analysis={analysis}
                  sidebarItemDatas={childSidebarItems}
                  selectedItemState={selectedItemState}
                  tier={tier + 1}
                  onOpenStepInitializationView={onOpenStepInitializationView}
                />
              </div>
            )}
            {actionButtonOptions && (analysis.permissions.writable || analysis.permissions.unarchivable) && (
              <div className={css(styles.childSidebarItemsWrapper, styles.addViewButtonWrapper)}>
                <AddChartViewPopover
                  analysis={analysis}
                  stepId={actionButtonOptions.stepDataReference.stepId}
                  dataFrameId={actionButtonOptions.dataFrameId}
                  onOpenStepInitializationView={onOpenStepInitializationView}
                >
                  {(openMenu) => (
                    <AddViewButton
                      disabledReason={
                        isAnalysisRunning(analysis)
                          ? 'Analysis is running'
                          : isArchived(analysis) && analysis.permissions.unarchivable
                            ? ARCHIVED_ANALYSIS_TOOLTIP_TEXT
                            : null
                      }
                      onClick={openMenu}
                    />
                  )}
                </AddChartViewPopover>
              </div>
            )}
          </div>
        )
      )}
    </>
  );
}

/** @package */
export interface SidebarSectionProps extends Omit<SidebarSectionItemsProps, 'tier'> {
  headerButton?: ReactNode;
  title: string;
}

/** @package */
export default function SidebarSection({
  headerButton,
  title,
  ...sidebarSectionItemsProps
}: SidebarSectionProps): JSX.Element {
  return (
    <>
      <div
        className={css(styles.sectionHeader)}
        {...getTestProps({component: 'SidebarSection', element: 'header'})}
      >
        <div className={css(styles.sectionTitle)}>{title}</div>
        {headerButton}
      </div>
      <SidebarSectionItems {...sidebarSectionItemsProps} />
    </>
  );
}

const styles = StyleSheet.create({
  sectionHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
    textTransform: 'uppercase',
    color: GRAY_DARKER,
    letterSpacing: 1,
  },
  childSidebarItemsWrapper: {
    marginLeft: 20,
  },
  addViewButtonWrapper: {
    marginTop: 8,
    marginBottom: 12,
    // Padding to make the add icon aligned with other child items in the section.
    paddingLeft: 7,
  },
});
